import { useEffect, useMemo, useState } from "react";

import { DateTime } from "luxon";

import { ClockIcon } from "@heroicons/react/24/outline";

import AssemblyFlow from "../../components/productionManager/AssemblyFlow";
import BayStationCard from "../../components/productionManager/BayStationCard";
import DueNextSection from "../../components/productionManager/DueNextSection";
import InProgressSessionsColumn from "../../components/productionManager/InProgressSessionsColumn";
import OverviewKpiCard from "../../components/productionManager/OverviewKpiCard";
import PartsRemovedKpiCard from "../../components/productionManager/PartsRemovedKpiCard";
import LoadingWheel from "../../components/shared/LoadingWheel";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import useProductionManagerOverview from "../../data/useProductionManagerOverview";
import { DismantlingBayType } from "../../models/DismantlingBay";
import { ProductionManagerView } from "../../models/ProductionManager";
import classNames from "../../utils/classNames";

const STORAGE_KEY = {
  view: "productionManager.view",
  hideStats: "productionManager.hideStats",
};

const readStoredView = (): ProductionManagerView => {
  if (typeof window === "undefined") return "Line";
  const value = window.localStorage.getItem(STORAGE_KEY.view);
  return value === "Bays" ? "Bays" : "Line";
};

const readStoredHideStats = (): boolean => {
  if (typeof window === "undefined") return false;
  return window.localStorage.getItem(STORAGE_KEY.hideStats) === "true";
};

export default function ProductionManager() {
  const { setInfo } = usePageNameContext();
  const [view, setView] = useState<ProductionManagerView>(readStoredView);
  const [hideStats, setHideStats] = useState<boolean>(readStoredHideStats);
  const [now, setNow] = useState<DateTime>(DateTime.local());

  const bayType =
    view === "Line" ? DismantlingBayType.ProductionLine : DismantlingBayType.Bay;
  const { overview } = useProductionManagerOverview(bayType);
  const data = overview.data?.data;

  useEffect(() => {
    setInfo({
      name: "Production Line Manager",
      desc: "Live view of vehicles being dismantled across the main line and off line stations",
    });
  }, [setInfo]);

  useEffect(() => {
    const interval = window.setInterval(() => setNow(DateTime.local()), 1000);
    return () => window.clearInterval(interval);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(STORAGE_KEY.view, view);
  }, [view]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(STORAGE_KEY.hideStats, String(hideStats));
  }, [hideStats]);

  const bays = useMemo(() => data?.bays ?? [], [data]);
  const assemblies = useMemo(() => data?.assemblies ?? [], [data]);
  const inProgressTriages = useMemo(() => data?.inProgressTriages ?? [], [data]);
  const inProgressDepollutions = useMemo(() => data?.inProgressDepollutions ?? [], [data]);
  const kpis = data?.kpis;

  return (
    <div className="mt-2 flex flex-col gap-4">
      {/* Header strip: clock + date + toggles */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white border border-gray-200 rounded-md p-3">
        <div className="flex items-center gap-3">
          <ClockIcon className="h-7 w-7 text-gray-400" />
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
              Current Time
            </div>
            <div className="text-3xl font-semibold text-gray-900 tabular-nums leading-none">
              {now.toFormat("HH:mm:ss")}
            </div>
          </div>
        </div>

        <div className="flex flex-col items-end gap-2">
          <div className="text-sm text-gray-600">{now.toFormat("cccc d LLLL yyyy")}</div>
          <div className="flex items-center gap-2">
            <div className="inline-flex rounded-md bg-gray-100 p-0.5 text-xs font-medium">
              <button
                type="button"
                onClick={() => setView("Line")}
                className={classNames(
                  "px-2.5 py-1 rounded",
                  view === "Line" ? "bg-gray-900 text-white" : "text-gray-700"
                )}
              >
                Line
              </button>
              <button
                type="button"
                onClick={() => setView("Bays")}
                className={classNames(
                  "px-2.5 py-1 rounded",
                  view === "Bays" ? "bg-gray-900 text-white" : "text-gray-700"
                )}
              >
                Bays
              </button>
            </div>
            <button
              type="button"
              onClick={() => setHideStats((p) => !p)}
              className="px-2.5 py-1 rounded border border-gray-200 bg-white text-xs font-medium text-gray-700 hover:bg-gray-50"
            >
              {hideStats ? "Show stats" : "Hide stats"}
            </button>
          </div>
        </div>
      </div>

      {overview.isLoading ? (
        <div className="flex-1 flex items-center justify-center bg-white border border-gray-200 rounded-md py-12">
          <LoadingWheel />
        </div>
      ) : (
        <>
          {/* KPIs */}
          {!hideStats && kpis && (
            <div className="flex flex-col gap-3 sm:flex-row sm:items-stretch">
              {view === "Line" ? (
                <OverviewKpiCard
                  label="Lines In Use"
                  value={`${kpis.linesInUse.used} / ${kpis.linesInUse.total}`}
                  footer="Production lines in use"
                />
              ) : (
                <OverviewKpiCard
                  label="Bays In Use"
                  value={`${kpis.baysInUse.used} / ${kpis.baysInUse.total}`}
                  footer="Bays in use"
                />
              )}
              <OverviewKpiCard
                label="Vehicles In Progress"
                value={kpis.vehiclesInProgress}
                footer="Across all stations"
              />
              <OverviewKpiCard
                label="Vehicles Dismantled Today"
                value={kpis.vehiclesDismantledToday}
                footer="Completed jobs"
              />
              <PartsRemovedKpiCard partsRemoved={kpis.partsRemoved} />
            </div>
          )}

          {/* Due next */}
          {bays.length > 0 && (
            <div>
              <div className="mb-2 flex items-baseline gap-2">
                <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-700">
                  Due Onto The Line Next
                </h2>
                <span className="text-[11px] text-gray-500">Sourced from the Production Planner</span>
              </div>
              <DueNextSection bays={bays} />
            </div>
          )}

          {/* Assembly flow — render every bay/line, including empty ones */}
          {bays.length > 0 && assemblies.length > 0 && (
            <div>
              <div className="mb-2 flex items-baseline gap-2">
                <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-700">
                  Assembly Flow
                </h2>
                <span className="text-[11px] text-gray-500">
                  Vehicles flow through assemblies left to right
                </span>
              </div>
              <AssemblyFlow bays={bays} assemblies={assemblies} now={now} />
            </div>
          )}

          {/* Station / Bay grid */}

          {view === "Bays" &&
            (bays.length === 0 ? (
              <div className="bg-white border border-gray-200 rounded-md p-8 text-center text-sm text-gray-500">
                No dismantling bays configured.
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-6 gap-3">
                {bays.map((bay) => (
                  <BayStationCard key={bay.id} bay={bay} now={now} />
                ))}
              </div>
            )
            )}

          {/* In-progress Triage / Depollution timers (driven by JobAssemblySession) */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <InProgressSessionsColumn
              title="Triage In Progress"
              sessions={inProgressTriages}
              now={now}
              routePrefix="triage"
            />
            <InProgressSessionsColumn
              title="Depollution In Progress"
              sessions={inProgressDepollutions}
              now={now}
              routePrefix="depollution"
            />
          </div>
        </>
      )}
    </div>
  );
}
